export interface BagistoErrorLike {
  status: number;
  message: Error;
  cause?: Error;
}

export const isObject = (
  object: unknown,
): object is Record<string, unknown> => {
  return (
    typeof object === "object" &&
    object !== null &&
    !Array.isArray(object) &&
    Object.keys(object).length > 0
  );
};

export const isArray = (arr: any) => {
  return arr && Array.isArray(arr) && arr.length > 0;
};

export const isBagistoError = (error: unknown): error is BagistoErrorLike => {
  if (!isObject(error)) return false;

  // ApolloError with GraphQL response (Bagisto style error)
  if ("graphQLErrors" in error && Array.isArray((error as any).graphQLErrors)) {
    return true;
  }

  // Standard JS error (still might be Bagisto wrapped)
  if (error instanceof Error) return true;

  return findError(error);
};

/**
 * Extracts the most-specific error message available from anything thrown by
 * `bagistoFetch`. That helper throws one of two plain-object shapes:
 *   1. `{ message, status, cause, query }` — when the inner error matched
 *      `isBagistoError` (top-level `message`).
 *   2. `{ error: <inner>, query }` — when it didn't (the real GraphQL error
 *      message is at `error.error.message`, e.g. for raw GraphQLError objects
 *      thrown from `body.errors[0]`).
 * Also handles standard `Error` instances and ApolloError-shaped objects.
 * Returns `undefined` when no usable string can be found, so callers can fall
 * back to a route-specific default.
 */
export const getBagistoErrorMessage = (error: unknown): string | undefined => {
  if (error instanceof Error) return error.message;
  if (!isObject(error)) {
    return typeof error === "string" ? error : undefined;
  }

  // Shape 1: top-level `message` (also covers ApolloError)
  if (typeof error.message === "string" && error.message.length > 0) {
    return error.message;
  }

  // Shape 2: nested `{ error: <inner>, query }`
  const inner = (error as { error?: unknown }).error;
  if (inner instanceof Error) return inner.message;
  if (isObject(inner)) {
    if (typeof inner.message === "string" && inner.message.length > 0) {
      return inner.message;
    }
  } else if (typeof inner === "string" && inner.length > 0) {
    return inner;
  } else if (inner != null) {
    return String(inner);
  }

  // ApolloError-shaped: graphQLErrors[0].message
  const gqlErrors = (error as { graphQLErrors?: unknown }).graphQLErrors;
  if (Array.isArray(gqlErrors) && gqlErrors.length > 0) {
    const first = gqlErrors[0];
    if (isObject(first) && typeof first.message === "string") {
      return first.message;
    }
  }

  return undefined;
};

function findError<T extends object>(error: T): boolean {
  if (Object.prototype.toString.call(error) === "[object Error]") {
    return true;
  }

  const prototype = Object.getPrototypeOf(error) as T | null;

  return prototype === null ? false : findError(prototype);
}
